import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Upload, ChevronLeft } from 'lucide-react';
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';
import { useUpdate, useChangePassword, useUser } from '../../../hooks/useAuth';
import PhoneInput from 'react-phone-input-2';
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../common/AddressAutoComplete'
import 'react-phone-input-2/lib/style.css';
import Modal from '../../common/ModalComponent';
import SingleSubmitForm from '../../common/SingleSubmitForm';

// Main App Component
const Profile = () => {
    const { isDarkMode } = useStore(themeStore)
    const { REACT_APP_IMAGE_URL } = process.env
    const updateMutation = useUpdate()
    const changePasswordMutation = useChangePassword()
    const navigate = useNavigate()
    const [email, setEmail] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [companyProfile, setCompanyProfile] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [countryCode, setCountryCode] = useState('+1')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [taxId, setTaxId] = useState('');
    const [companyDocument, setCompanyDocument] = useState(null); // optional
    const [userType, setUserType] = useState(2);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfPassword, setShowConfPassword] = useState(false);
    
    const { data: userResponse, isSuccess: isSuccessUser } = useUser()
    const [selectedAddress, setSelectedAddress] = useState({
        coordinates: [],
        type: "point",
        name: ""
    });

    const colors = {
        text: isDarkMode ? '#e0e0e0' : '#333333',
        border: isDarkMode ? '#333333' : '#e0e0e0',
        inputBackground: isDarkMode ? '#2d2d2d' : '#ffffff'
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCompanyProfile(file); // store file
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
        }
    };

    const handleChangePassword = () => {
        if (oldPassword.trim() === "") {
            toast.error("Old Password is not allowed to empty")
            return
        }
        if (password.trim() === "" || confPassword.trim() === "") {
            toast.error("Password is not allowed to empty")
            return
        }
        if (password.trim() !== confPassword.trim()) {
            toast.error("Password and Confirm Password should be matched")
            return
        }
        changePasswordMutation.mutate({
            old_password: oldPassword,
            new_password: password
        }, {
            onSuccess: () => {
                setShowChangePasswordModal(false)
                toast.success("Password has been updated successfully")
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Update failed"
                toast.error(message)
            }
        })
    }

    const handleEditProfile = async () => {
        if (companyName.trim() === "") {
            toast.error("Company Name is not allowed to empty")
            return
        }
        if (countryCode.trim() === "") {
            toast.error("Please Select Valid Country Code")
            return
        }
        if (phoneNumber.trim() === "") {
            toast.error("Phone Number is not allowed to empty")
            return
        }
        if (selectedAddress?.name === "") {
            toast.error("Please Select Valid Address")
            return
        }
        const formData = new FormData();
        formData.append("name", companyName);
        formData.append("country_code", countryCode);
        formData.append("phone_number", phoneNumber);
        formData.append("address", JSON.stringify(selectedAddress));
        formData.append("user_type", userType);
        // optional
        formData.append("taxId_number", taxId);
        if (companyDocument) {
            formData.append("company_document", companyDocument);
        }
        if (companyProfile) {
            formData.append("company_logo", companyProfile);
        }
        try {
            updateMutation.mutate(formData, {
                onSuccess() {
                    toast.success("Profile Updated Successfully")
                    navigate(-1)
                },
                onError(err) {
                    const message = err.response?.data?.message || "Update failed"
                    toast.error(message)
                }
            })
        } catch (error) {
            const message = error.response?.data?.message || "Profile Update Failed"
            toast.error(message)
        }
    };

    const handlePhoneChange = (value, data) => {
        setPhoneNumber(value.slice(data.dialCode.length)) // only the number after country code
        setCountryCode(data.dialCode) // country code as number, e.g. '1'
    }

    useEffect(() => {
        if (isSuccessUser) {
            // loadprofile
            const userProfile = userResponse?.data?.data
            if (userProfile?.profile_pic) {
                setImagePreview(`${REACT_APP_IMAGE_URL}/${userProfile?.profile_pic}`)
            }
            setCompanyName(userProfile?.name)
            setEmail(userProfile?.email)
            setCountryCode(userProfile?.country_code)
            setPhoneNumber(userProfile?.phone_number)
            setSelectedAddress(userProfile?.address)
            setTaxId(userProfile?.taxId_number)
            setUserType(userProfile?.user_type)
        }
    }, [isSuccessUser, userResponse, REACT_APP_IMAGE_URL])

    return (
        <>
            <div className={`min-h-screen items-center justify-center p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-800'}`}>
                <div className="w-[75%] mt-6 mx-auto flex mb-6">
                    <button onClick={() => navigate(-1)} className="mr-4 p-2 border hover:bg-gray-100 rounded-lg transition-colors">
                        <ChevronLeft className="w-6 h-6 text-gray-600" />
                    </button>
                    <p className="w-full text-3xl poppins-regular text-gray-900 mt-1" style={{ textAlign: "center" }} >EDIT PROFILE</p>
                </div>
                <div className={`self-center mx-auto w-[50%] px-[100px] py-[20px] rounded-lg shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <SingleSubmitForm onSubmit={handleEditProfile}>
                        <div className="p-6">
                            <div className="mb-6 flex flex-col items-center">
                                <div className={`w-16 h-16 border-2 border-dashed rounded-md flex items-center justify-center mb-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                    ) : (
                                        <Upload size={16} className="text-blue-600" />
                                    )}
                                    <input
                                        type='file'
                                        style={{ position: 'absolute', height: "65px", width: '65px', opacity: 0, cursor: 'pointer' }}
                                        accept='image/*'
                                        onChange={handleImageChange}
                                    />
                                </div>
                                <span className="text-sm text-gray-500">Company logo/image</span>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Company Name*
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className={`w-full p-3 rounded border ${isDarkMode
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300'
                                            }`}
                                    />
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Email*
                                </label>
                                <div className="relative">
                                    <input
                                        disabled={true}
                                        style={{ cursor: 'not-allowed' }}
                                        value={email}
                                        className={`w-full p-3 rounded border ${isDarkMode
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300'
                                            }`}
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <Mail size={18} className="text-blue-600" />
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Contact number*
                                </label>
                                <div className="flex">
                                    <PhoneInput
                                        country={'us'}
                                        value={countryCode + phoneNumber}
                                        onChange={handlePhoneChange}
                                        inputStyle={{
                                            width: '100%',
                                            backgroundColor: isDarkMode ? '#374151' : '#fff',
                                            color: isDarkMode ? '#fff' : '#000',
                                            borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
                                            borderRadius: '0.375rem',
                                            padding: '22px 50px',
                                            fontSize: '1rem',
                                        }}
                                        buttonStyle={{
                                            backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                                            borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
                                        }}
                                        containerStyle={{
                                            width: '100%',
                                            height: '45px'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Address*
                                </label>
                                <AddressAutocomplete
                                    placeholder={"Enter address"}
                                    value={selectedAddress}
                                    onSelect={(val) => setSelectedAddress(val)}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    Tax Identification number
                                </label>
                                <input
                                    type="text"
                                    value={taxId}
                                    onChange={(e) => setTaxId(e.target.value)}
                                    className={`w-full p-3 rounded border ${isDarkMode
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300'
                                        }`}
                                />
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-1">
                                    Organization Director/Share Holder ID
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf,image/*"
                                    onChange={(e) => setCompanyDocument(e.target.files[0])}
                                    className={`w-full p-2 rounded border ${isDarkMode
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300'
                                        }`}
                                />
                            </div>
                            <div className='text-center'>
                                <button
                                    type='button'
                                    onClick={() => setShowChangePasswordModal(true)}
                                    className="w-[200px] poppins-regular bg-white-600 border border-blue-500 mb-4 text-blue-500 font-bold py-3 px-4 rounded-full">
                                    Change Password
                                </button>
                            </div>
                            <div className='text-center'>
                                <button
                                    type='submit'
                                    disabled={updateMutation?.isLoading ? true : false}
                                    className="w-[200px] poppins-regular bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-full focus:outline-none focus:shadow-outline">
                                    {updateMutation.isLoading ? "Please Wait...." : "Update"}
                                </button>
                            </div>
                        </div>
                    </SingleSubmitForm>
                </div>
            </div>
            {/* Change Password Modal */}
            <Modal isDarkMode={isDarkMode} key={"changepasswordmodal"} title="Change Password" isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)}>
                <div className="mb-6">
                    <div className="mb-4">
                        <label className={`block ${isDarkMode?"text-white" : "text-gray-800"} text-sm font-medium mb-1`}>
                            Old Password*
                        </label>
                        <div className="relative">
                            <input
                                type={showOldPassword ? "text" : "password"}
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                className={`w-full p-3 rounded border ${isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300'
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowOldPassword(!showOldPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {showOldPassword ?
                                    <EyeOff size={18} className="text-blue-600" /> :
                                    <Eye size={18} className="text-blue-600" />
                                }
                            </button>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className={`block ${isDarkMode?"text-white" : "text-gray-800"} text-sm font-medium mb-1`}>
                            New Password*
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={`w-full p-3 rounded border ${isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300'
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {showPassword ?
                                    <EyeOff size={18} className="text-blue-600" /> :
                                    <Eye size={18} className="text-blue-600" />
                                }
                            </button>
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className={`block ${isDarkMode?"text-white" : "text-gray-800"} text-sm font-medium mb-1`}>
                            Confirm New Password*
                        </label>
                        <div className="relative">
                            <input
                                type={showConfPassword ? "text" : "password"}
                                value={confPassword}
                                onChange={(e) => setConfPassword(e.target.value)}
                                className={`w-full p-3 rounded border ${isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300'
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfPassword(!showConfPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {showConfPassword ?
                                    <EyeOff size={18} className="text-blue-600" /> :
                                    <Eye size={18} className="text-blue-600" />
                                }
                            </button>
                        </div>
                    </div>
                    <div className='text-center mt-5'>
                        <button
                            onClick={() => setShowChangePasswordModal(false)}
                            className={`w-[200px] py-3 text-center font-medium ${isDarkMode?"hover:bg-gray-700":"hover:bg-gray-100"} transition rounded-full mr-2`}
                            style={{ color: colors.text }}>
                            Cancel
                        </button>
                        <button
                            onClick={handleChangePassword}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mb-3">
                            Confirm
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
export default Profile